import type { TestableComponent } from '@mweb/types';
import { type TabsTriggerProps } from '@radix-ui/react-tabs';

import { TabsContent as RadixTabsContent } from 'components/productdetail/ReviewSummarization/Tabs';
import useMartyContext from 'hooks/useMartyContext';
import { cn } from 'helpers/classnames';
import IconButton from 'components/common/IconButton';
import type { ReviewSummarizationResponseAspects } from 'types/reviewSummarization';
import UtilityStrokeCloseMediumIcon from 'tailwind/components/Icons/UtilityStrokeCloseMediumIcon';

export interface TabsContentProps
  extends Omit<TabsTriggerProps, 'value'>,
    Pick<ReviewSummarizationResponseAspects, 'totalcount' | 'negativecount' | 'positivecount'>,
    TestableComponent {
  headerName: string;
  handleOpenTab: (tab: string) => void;
}

export const TabsContent = (props: TabsContentProps) => {
  const { testId, children, headerName, positivecount, negativecount, totalcount, handleOpenTab } = props;
  const { testId: getTestId } = useMartyContext();

  return (
    <RadixTabsContent value={headerName} data-test-id={getTestId(testId)}>
      <article>
        <header className="flex flex-wrap gap-y-3">
          <h2 className="m-0 flex w-full gap-x-6">
            <span className="flex flex-wrap gap-x-4">
              <TabsContentHeaderWrapper>
                <TabsContentHeaderLeftContent>{totalcount.toLocaleString()}</TabsContentHeaderLeftContent>
                <TabsContentHeaderRightContent className="text-tertiary">Customers mention</TabsContentHeaderRightContent>
              </TabsContentHeaderWrapper>
              {!!positivecount && (
                <TabsContentHeaderWrapper>
                  <TabsContentHeaderLeftContent className="text-success-inline">{positivecount.toLocaleString()}</TabsContentHeaderLeftContent>
                  <TabsContentHeaderRightContent className="text-success-inline">Positive</TabsContentHeaderRightContent>
                </TabsContentHeaderWrapper>
              )}
              {!!negativecount && (
                <TabsContentHeaderWrapper>
                  <TabsContentHeaderLeftContent className="text-alert-inline">{negativecount.toLocaleString()}</TabsContentHeaderLeftContent>
                  <TabsContentHeaderRightContent className="text-alert-inline">Negative</TabsContentHeaderRightContent>
                </TabsContentHeaderWrapper>
              )}
            </span>
            <IconButton className="ml-auto hidden sm:flex" label="close" size="base" variant="icon" onClick={() => handleOpenTab('')} data-test-id={getTestId('ReviewSummarizationAttributeCloseButton')}>
              <UtilityStrokeCloseMediumIcon size={24} />
            </IconButton>
          </h2>
          {children}
        </header>
      </article>
    </RadixTabsContent>
  );
};

const TabsContentHeaderWrapper = ({ children }: React.ComponentPropsWithoutRef<'span'>) => (
  <span className="flex flex-col flex-wrap gap-x-3 sm:flex-row sm:items-center">{children}</span>
);

const TabsContentHeaderLeftContent = ({ children, className }: React.ComponentPropsWithoutRef<'span'>) => (
  <span className={cn('text-lg font-bold', className)}>{children}</span>
);

const TabsContentHeaderRightContent = ({ children, className }: React.ComponentPropsWithoutRef<'span'>) => (
  <span className={cn('text-sm', className)}>{children}</span>
);
